import { type Channel, type Partner, partners } from "@expert/shared-types";

const IS_DEV = import.meta.env.VITE_ENV_TYPE === "development";

export const SERVICE_PROVIDER_QS_KEY = "serviceProvider";
export const ALLOWED_PARTNER_QS_KEY = "allowedPartner";

export const serviceProviders = Object.freeze(["AMAZON_CONNECT", "TWILIO", "MOCK_PROVIDER"] as const);

export type ServiceProvider = (typeof serviceProviders)[number];

export function isServiceProvider(value: unknown): value is ServiceProvider {
    return typeof value === "string" && (serviceProviders as readonly string[]).includes(value);
}

export function isAllowedPartner(value: unknown): value is Partner {
    return typeof value === "string" && (["No partners", ...partners] as readonly string[]).includes(value);
}

export function assertServiceProvider(value: unknown): asserts value is ServiceProvider {
    if (isServiceProvider(value)) return;
    throw new Error(`value is not a ServiceProvider: ${value?.toString()}`);
}

export function assertAllowedPartner(value: unknown): asserts value is Partner {
    if (isAllowedPartner(value)) return;
    throw new Error(`value is not an allowedPartner: ${value?.toString()}`);
}

export function getServiceProvidersOverride() {
    if (!IS_DEV) return;
    const values = new URLSearchParams(window.location.search).getAll(SERVICE_PROVIDER_QS_KEY);
    if (values.length === 0) return;
    for (const value of values) assertServiceProvider(value);
    return values as ServiceProvider[];
}

export function getAllowedPartnersOverride() {
    if (!IS_DEV) return;
    const values = new URLSearchParams(window.location.search).getAll(ALLOWED_PARTNER_QS_KEY);
    if (values.length === 0) return;
    for (const value of values) assertAllowedPartner(value);
    return values as Partner[];
}

// TODO: allow multi-partner configuration.
const AMAZON_CONNECT_PARTNERS = [
    "cricket",
    "mcafee",
    "adt",
    "microsoft",
    "life360",
    "amazonrouter",
    "youtubetv",
    "demo",
];
export function getServiceProviders(partner: Partner | undefined, channel: Channel): ServiceProvider[] {
    // serviceProviderOverride should already be undefined in Prod,
    // but leaving the IS_DEV check here for extra safety
    const serviceProviderOverride = getServiceProvidersOverride();
    if (IS_DEV && Array.isArray(serviceProviderOverride)) {
        return serviceProviderOverride;
    }

    if (channel === "chat") {
        return ["TWILIO"];
    }

    if (partner && AMAZON_CONNECT_PARTNERS.includes(partner)) {
        return ["AMAZON_CONNECT"];
    }

    return ["TWILIO"];
}

export function selectPartnerWithOverride(partner: Partner): Partner {
    const partnerOverride = getAllowedPartnersOverride();
    return partnerOverride ? partnerOverride[0] : partner;
}
